import React, { useEffect, useRef, useState } from 'react';

import {
  StyledProfileContainer,
  StyledAction,
  StyledLink,
  StyledUserAndRewardSection,
  StyledRewardSection,
  StyledRewardValue,
  StyledPaper,
  StyledPopper,
} from './styles';
import { useHeaderContext } from '../../HeaderContextProvider';
import { NavigationMenuType } from '../../types';

import { DownArrow } from '../../assets/DownArrow';
import { UpArrow } from '../../assets/UpArrow';
import { PersonIcon } from '../../assets/PersonIcon';
import { SettingsIcon } from '../../assets/SettingsIcon';
import { ExitAppIcon } from '../../assets/ExitAppIcon';
import { RewardIcon } from '../../assets/RewardIcon';

const ProfileMenu = () => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    programUrl,
    canAccessBo,
    routes,
    isStoreEnabled,
    userFirstname,
    totalRewardsValue,
    t,
    colorsPaletteFromBO,
    useLogout,
  } = useHeaderContext();

  const logout = useLogout();

  const DropIcon = profileMenuOpen ? UpArrow : DownArrow;

  const navigationMenus: NavigationMenuType[] = [
    {
      id: 'account',
      label: t('menu.account'),
      type: 'link',
      link: `${programUrl}${routes.STORE_ACCOUNT}`,
      Icon: <PersonIcon color={colorsPaletteFromBO.main} size={'25'} />,
    },
    ...(canAccessBo
      ? [
          {
            id: 'admin',
            label: t('menu.admin_panel'),
            type: 'link',
            link: `${programUrl}${routes.ADMIN_PANEL}`,
            Icon: <SettingsIcon color={colorsPaletteFromBO.main} size={'30'} />,
          },
        ]
      : []),
    {
      id: 'logout',
      label: t('auth.logout'),
      type: 'action',
      action: logout,
      Icon: <ExitAppIcon color={colorsPaletteFromBO.main} size={'30'} />,
    },
  ];

  const handleClickAway = (event: MouseEvent) => {
    if (
      anchorRef.current &&
      !anchorRef.current.contains(event.target as Node) &&
      menuRef.current &&
      !menuRef.current.contains(event.target as Node)
    ) {
      setProfileMenuOpen(false);
    }
  };

  useEffect(() => {
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickAway);
    } else {
      document.removeEventListener('mousedown', handleClickAway);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
    };
  }, [profileMenuOpen]);

  return (
    <>
      <StyledProfileContainer
        ref={anchorRef}
        onClick={() => setProfileMenuOpen(!profileMenuOpen)}
      >
        <StyledUserAndRewardSection>
          {userFirstname}
          {isStoreEnabled && (
            <StyledRewardSection>
              <RewardIcon color="#f2b308" size="32" />
              <StyledRewardValue colorsPaletteFromBO={colorsPaletteFromBO}>
                {totalRewardsValue}
              </StyledRewardValue>
            </StyledRewardSection>
          )}
        </StyledUserAndRewardSection>
        <DropIcon size="16px" />
      </StyledProfileContainer>

      {profileMenuOpen && (
        <StyledPopper>
          <StyledPaper ref={menuRef}>
            <>
              {navigationMenus.map(
                ({
                  id,
                  label,
                  type,
                  action,
                  link,
                  Icon,
                }: NavigationMenuType) => {
                  switch (type) {
                    case 'action':
                      return (
                        <StyledAction
                          key={id}
                          onClick={action}
                          colorsPaletteFromBO={colorsPaletteFromBO}
                        >
                          <div>{Icon}</div>
                          <div>{label}</div>
                        </StyledAction>
                      );
                    case 'link':
                      return (
                        <StyledLink
                          key={id}
                          href={link || ''}
                          colorsPaletteFromBO={colorsPaletteFromBO}
                        >
                          <div>{Icon}</div>
                          <div>{label}</div>
                        </StyledLink>
                      );
                    default:
                      return null;
                  }
                },
              )}
            </>
          </StyledPaper>
        </StyledPopper>
      )}
    </>
  );
};

export default ProfileMenu;
